import React, { useEffect, useState, useCallback } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./discounts.module.css";

const naira = (v) => `₦${new Intl.NumberFormat("en-NG").format(v || 0)}`;

async function readError(response, fallback) {
  const text = await response.text();
  try {
    return JSON.parse(text).message || fallback;
  } catch {
    return text || fallback;
  }
}

const DeliveryZones = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ name: "", fee: "" });
  const [saving, setSaving] = useState(false);
  const canManage = canManageProducts();

  const fetchZones = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/delivery-zones`, {
        headers: authHeader(),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not load delivery areas."));
      }
      setZones(await response.json());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/delivery-zones`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ name: form.name, fee: Number(form.fee) }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not add the area."));
      }
      setForm({ name: "", fee: "" });
      flash(`${form.name} added — live in the checkout immediately.`);
      fetchZones();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateZone = async (zone, patch, message) => {
    setError("");
    try {
      const response = await fetch(`${API_URL}/delivery-zones/${zone._id || zone.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(patch),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not update the area."));
      }
      flash(message);
      fetchZones();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChangeFee = (zone) => {
    const input = window.prompt(`New delivery fee for ${zone.name} (₦):`, zone.fee);
    if (input === null) return;
    const fee = Number(input);
    if (Number.isNaN(fee) || fee < 0) {
      setError("The fee must be a number of naira, 0 or more.");
      return;
    }
    updateZone(zone, { fee }, `${zone.name} is now ${naira(fee)}.`);
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`Remove ${zone.name}? Shoppers will no longer see it at checkout.`)) {
      return;
    }
    setError("");
    try {
      const response = await fetch(`${API_URL}/delivery-zones/${zone._id || zone.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not delete the area."));
      }
      flash(`${zone.name} removed.`);
      fetchZones();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Delivery Areas</h1>
          <p className={styles.subtitle}>
            The areas and fees shoppers see at checkout. Changes go live
            immediately — no redeploy needed.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.success}>{notice}</p>}

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Areas</h2>
          {loading ? (
            <p className={styles.loading}>Loading areas…</p>
          ) : zones.length === 0 ? (
            <p className={styles.loading}>No areas yet — add the first one below.</p>
          ) : (
            <ul className={styles.list}>
              {zones.map((zone) => (
                <li key={zone._id || zone.id} className={styles.row}>
                  <div className={styles.rowInfo}>
                    <span className={zone.active ? styles.code : styles.codeInactive}>
                      {zone.name}
                    </span>
                    <span className={styles.rowLabel}>{naira(zone.fee)} delivery</span>
                  </div>
                  <span className={zone.active ? styles.statusChip : styles.statusChipOff}>
                    {zone.active ? "Active" : "Hidden"}
                  </span>
                  {canManage && (
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => handleChangeFee(zone)}
                      >
                        Change fee
                      </button>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() =>
                          updateZone(
                            zone,
                            { active: !zone.active },
                            `${zone.name} is now ${zone.active ? "hidden from" : "visible in"} checkout.`
                          )
                        }
                      >
                        {zone.active ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        onClick={() => handleDelete(zone)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canManage && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Add an area</h2>
            <form onSubmit={handleCreate}>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="z-name">Area name</label>
                  <input
                    id="z-name"
                    className={styles.input}
                    required
                    placeholder="Ajah"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="z-fee">Delivery fee (₦)</label>
                  <input
                    id="z-fee"
                    className={styles.input}
                    type="number"
                    required
                    min="0"
                    placeholder="5000"
                    value={form.fee}
                    onChange={(e) => setForm({ ...form, fee: e.target.value })}
                  />
                </div>
              </div>
              <button className={styles.submit} type="submit" disabled={saving}>
                {saving ? "Adding…" : "Add area"}
              </button>
            </form>
          </section>
        )}
      </div>
    </div>
  );
};

export default DeliveryZones;
